/**
 * The roster sheet — ADR-035 §3.2, the per-body columns of a batch package.
 *
 * `roster-sheet.json` lives in the SOURCE folder beside the bodies and is the
 * single source of what differs between them: the name, the id, the artist,
 * the tags, the avatar picker's facets, a declared height, the selection clip, the roles, a skip with its
 * reason. Everything the batch does per body it reads from a row here; the
 * batch never guesses from a filename it was not told about.
 *
 * Four rules, each pinned by a test:
 *  - A row per file, a file per row. `reconcileSheet` names the bodies with
 *    no row and the rows with no body — REPORTED, never packaged or invented.
 *  - The name is the merge key. `fillRow` resolves the id in ADR-033's order
 *    (the row's → the live roster's same-name row → `slugify(name)`) and the
 *    winner is WRITTEN BACK, so the next run replaces the same roster slot.
 *  - Blanks fill deterministically, never randomly: a missing name comes from
 *    the names pool by the body's sha256; a missing selection clip from the
 *    sheet's pool by the same hash. Both are written back — a name that
 *    differed between runs would be a NEW roster row.
 *  - A height is declared, not absent: no `heightMeters` on the row → the
 *    sheet's default, provenance `default`, as the one-avatar door does.
 *
 * Pure: parse, reconcile, fill, compose the choices, serialise. The app owns
 * the files (reading the folder, hashing the bodies, writing the sheet back).
 * Depends on: zod, ir/errors, session/document, session/package,
 * session/library, session/names-pool, session/picker-facets. Depended on by: the app's batch door.
 */
import { z } from 'zod';
import { SchemaValidationError } from '../ir/errors.js';
import {
  clipIdSchema,
  packageFacetsSchema,
  packageRoleSchema,
  pickerFacetsSchema,
  selectorPathSchema,
  slugSchema,
  type PackageChoices,
  type PickerFacets,
} from './document.js';
import { findRosterRow, type Roster } from './library.js';
import { NAMES_POOL, pickName, type PoolFamily } from './names-pool.js';
import { HEIGHT_METERS_BAND, PackageRefusal, slugify } from './package.js';
import { pickerFacetIssues, resolvePickerFacets } from './picker-facets.js';

export const ROSTER_SHEET_SCHEMA_VERSION = '1.0.0';

const heightSchema = z.number().gt(HEIGHT_METERS_BAND.min).lte(HEIGHT_METERS_BAND.max);

/**
 * World's single-logical-name rule, verbatim from their ingest (PR #1639,
 * `scripts/ingest-avatar-package.mjs` `SINGLE_NAME`): one capitalised word,
 * letters only; components joined by an apostrophe or hyphen, each later one
 * capitalised or all-lowercase; a single letter is a name; combining marks
 * ride their letter; NFC first. `dancingSquirrel` and `Alissa Tripo` are
 * refused by their door — the sheet refuses them first, where the fix is.
 */
const CAP = String.raw`[\p{Lu}\p{Lt}]\p{M}*(?:\p{Ll}\p{M}*)*`;
const LOW = String.raw`(?:\p{Ll}\p{M}*)+`;
export const SINGLE_NAME = new RegExp(String.raw`^${CAP}(?:['’-](?:${CAP}|${LOW}))*$`, 'u');

/** World's reason a name is not a single logical name, or null when it is. */
export function singleNameReason(name: string): string | null {
  return SINGLE_NAME.test(name.normalize('NFC')) ? null : `"${name}" is not a single logical name (one capitalised word, e.g. "Squirrel") — World's ingest refuses it (#1639); rename in the sheet`;
}

export const rosterSheetRowSchema = z
  .strictObject({
    /** The body's filename inside the source folder (no path). */
    file: z.string().regex(/^[^/\\]+\.glb$/i),
    name: z.string().trim().min(1).optional(),
    id: slugSchema.optional(),
    artist: z.string().trim().min(1).optional(),
    tags: z.array(z.string().trim().min(1)).optional(),
    heightMeters: heightSchema.optional(),
    selectionClip: clipIdSchema.optional(),
    roles: z.array(packageRoleSchema).min(1).optional(),
    facets: packageFacetsSchema.optional(),
    /**
     * World's avatar-picker filters for this body. Per facet, absent = the
     * roster row it replaces keeps its own (carried at compose, never written
     * back here: the prod manifest stays the place a later review edits).
     */
    pickerFacets: pickerFacetsSchema.optional(),
    npc: z.strictObject({ archetype: selectorPathSchema, note: z.string().optional() }).optional(),
    /** Present = the batch leaves this body alone, and the report says why. */
    skip: z.string().trim().min(1).optional(),
  })
  .superRefine((r, ctx) => {
    if (r.roles !== undefined && new Set(r.roles).size !== r.roles.length) ctx.addIssue({ code: 'custom', path: ['roles'], message: 'a role appears twice' });
    if (r.npc !== undefined && r.roles !== undefined && !r.roles.includes('npc')) {
      ctx.addIssue({ code: 'custom', path: ['npc'], message: 'an npc block without the npc role is a contradiction' });
    }
    // A closed picker vocabulary broken refuses the SHEET, listed with the rest
    // (the composer would refuse the body anyway — here it is said before any opens).
    if (r.pickerFacets !== undefined && r.skip === undefined) {
      for (const message of pickerFacetIssues(r.pickerFacets).refusals) ctx.addIssue({ code: 'custom', path: ['pickerFacets'], message });
    }
  });
export type RosterSheetRow = z.infer<typeof rosterSheetRowSchema>;

export const rosterSheetDefaultsSchema = z.strictObject({
  artist: z.string().trim().min(1),
  set: slugSchema,
  roles: z.array(packageRoleSchema).min(1),
  tags: z.array(z.string().trim().min(1)),
  facets: packageFacetsSchema,
  heightMeters: heightSchema,
  npc: z.strictObject({ archetype: selectorPathSchema, note: z.string().optional() }).optional(),
});
export type RosterSheetDefaults = z.infer<typeof rosterSheetDefaultsSchema>;

interface SheetIssue {
  readonly path: string;
  readonly message: string;
}

/**
 * The rules that span rows — a file, a name, an id each claimed once; the
 * defaults and the pool consistent. Run over the RAW shape (whatever fields
 * parse as strings), so a sheet with a shape error in one row still has its
 * cross-row errors LISTED beside it: zod stops the object refinement when a
 * property fails, and "every issue listed" is the contract here.
 */
function crossRowIssues(raw: unknown): SheetIssue[] {
  const out: SheetIssue[] = [];
  if (typeof raw !== 'object' || raw === null) return out;
  const s = raw as { defaults?: unknown; selectionPool?: unknown; rows?: unknown };
  const d = s.defaults as { roles?: unknown; npc?: unknown } | undefined;
  if (Array.isArray(d?.roles) && new Set(d.roles).size !== d.roles.length) out.push({ path: 'defaults.roles', message: 'a role appears twice' });
  if (d?.npc !== undefined && Array.isArray(d.roles) && !d.roles.includes('npc')) out.push({ path: 'defaults.npc', message: 'an npc block without the npc role is a contradiction' });
  if (Array.isArray(s.selectionPool) && new Set(s.selectionPool).size !== s.selectionPool.length) out.push({ path: 'selectionPool', message: 'a clip appears twice' });
  if (!Array.isArray(s.rows)) return out;
  const files = new Map<string, number>();
  const names = new Map<string, number>();
  const ids = new Map<string, number>();
  s.rows.forEach((row: unknown, i: number) => {
    if (typeof row !== 'object' || row === null) return;
    const r = row as { file?: unknown; name?: unknown; id?: unknown; skip?: unknown };
    if (typeof r.file === 'string') {
      const f = r.file.toLowerCase();
      if (files.has(f)) out.push({ path: `rows.${i}.file`, message: `"${r.file}" already has a row (row ${files.get(f)})` });
      files.set(f, i);
    }
    if (r.skip !== undefined) return; // a skipped row claims no name and no slot
    // Picker facets TYPED as tags (kind:monster, a bare kind:, two kinds) — the row's plus the
    // defaults', as choicesForRow will hand them to the compose — refuse the SHEET before any body
    // opens, like the pickerFacets column (Codex P2, #231). The column's own issues are its refinement's.
    const strings = (x: unknown): string[] => (Array.isArray(x) ? x.filter((t): t is string => typeof t === 'string').map((t) => t.trim()) : []);
    const typed = [...new Set([...strings((d as { tags?: unknown } | undefined)?.tags), ...strings((row as { tags?: unknown }).tags)])];
    if (typed.length > 0) {
      // Only a column that PARSES reaches the typed helpers; a malformed one is the row schema's issue,
      // listed with the rest — never a TypeError here (Codex P2, #231).
      const parsedField = pickerFacetsSchema.safeParse((row as { pickerFacets?: unknown }).pickerFacets);
      const field: PickerFacets | undefined = parsedField.success ? parsedField.data : undefined;
      const own = new Set(field === undefined ? [] : pickerFacetIssues(field).refusals);
      for (const message of resolvePickerFacets({ tags: typed, ...(field !== undefined ? { pickerFacets: field } : {}) }, null).refusals) {
        if (!own.has(message)) out.push({ path: `rows.${i}.tags`, message });
      }
    }
    if (typeof r.name === 'string') {
      const shape = singleNameReason(r.name.trim());
      if (shape !== null) out.push({ path: `rows.${i}.name`, message: shape });
      const n = r.name.trim().toLocaleLowerCase();
      if (names.has(n)) out.push({ path: `rows.${i}.name`, message: `"${r.name}" is also row ${names.get(n)}'s name — a name is the merge key, two bodies cannot share one` });
      names.set(n, i);
    }
    if (typeof r.id === 'string') {
      if (ids.has(r.id)) out.push({ path: `rows.${i}.id`, message: `id "${r.id}" is also row ${ids.get(r.id)}'s` });
      ids.set(r.id, i);
    }
  });
  return out;
}

export const rosterSheetSchema = z
  .strictObject({
    schemaVersion: z.literal(ROSTER_SHEET_SCHEMA_VERSION),
    defaults: rosterSheetDefaultsSchema,
    /** Allen's curated loop clips; empty = every row must name its own (or the batch refuses the row). */
    selectionPool: z.array(clipIdSchema),
    rows: z.array(rosterSheetRowSchema),
  })
  .superRefine((s, ctx) => {
    for (const issue of crossRowIssues(s)) ctx.addIssue({ code: 'custom', path: issue.path.split('.').map((seg) => (/^\d+$/.test(seg) ? Number(seg) : seg)), message: issue.message });
  });
export type RosterSheet = z.infer<typeof rosterSheetSchema>;

/**
 * Parse a sheet, every issue listed — a sheet that fails validation packages
 * nothing. `expect.set` is the shelf THIS packager writes onto (the app's
 * `PACKAGE_SET`): a sheet declaring another is refused here rather than
 * silently packaged onto the wrong shelf (Codex P2, #162 round three) — the
 * record's `set` comes from the packager, never from the sheet.
 */
export function parseRosterSheet(raw: unknown, where = 'roster-sheet.json', expect: { readonly set?: string } = {}): RosterSheet {
  const result = rosterSheetSchema.safeParse(raw);
  if (result.success && expect.set !== undefined && result.data.defaults.set !== expect.set) {
    throw new SchemaValidationError(where, [`defaults.set: "${result.data.defaults.set}" is not the shelf this packager writes onto ("${expect.set}") — the sheet must say the same set, or be packaged by the Harry that owns that shelf`]);
  }
  if (!result.success) {
    const lines = result.error.issues.map((i) => `${i.path.length > 0 ? i.path.map(String).join('.') : '(root)'}: ${i.message}`);
    // The cross-row rules again over the raw: zod skips the refinement once a
    // property failed, and a sheet must hear about ALL of it in one read.
    for (const c of crossRowIssues(raw)) {
      const line = `${c.path}: ${c.message}`;
      if (!lines.includes(line)) lines.push(line);
    }
    throw new SchemaValidationError(where, lines);
  }
  return result.data;
}

/** The sheet as the folder holds it: 2-space JSON, one trailing newline. */
export function serializeRosterSheet(sheet: RosterSheet): string {
  return `${JSON.stringify(sheet, null, 2)}\n`;
}

/** A fresh sheet for a folder: one row per body, named from nothing — the fill names them. */
export function emptyRosterSheet(defaults: RosterSheetDefaults, files: readonly string[]): RosterSheet {
  return { schemaVersion: ROSTER_SHEET_SCHEMA_VERSION, defaults, selectionPool: [], rows: files.map((file) => ({ file })) };
}

export interface SheetReconciliation {
  /** Bodies in the folder with no row — reported, not packaged. */
  readonly bodiesWithoutRows: readonly string[];
  /** Rows whose body is not in the folder — reported, not packaged. */
  readonly rowsWithoutBodies: readonly string[];
  /** Rows that will package: a body in the folder, not skipped. In sheet order. */
  readonly packageable: readonly RosterSheetRow[];
  /** Rows the sheet skips, with their reasons. */
  readonly skipped: readonly { readonly file: string; readonly reason: string }[];
}

/** A row per file, a file per row — the folder's GLB names against the sheet's, case-insensitively (OneDrive is). */
export function reconcileSheet(sheet: RosterSheet, folderGlbs: readonly string[]): SheetReconciliation {
  const inFolder = new Map(folderGlbs.map((f) => [f.toLowerCase(), f]));
  const inSheet = new Set(sheet.rows.map((r) => r.file.toLowerCase()));
  const packageable: RosterSheetRow[] = [];
  const skipped: { file: string; reason: string }[] = [];
  const rowsWithoutBodies: string[] = [];
  for (const row of sheet.rows) {
    if (!inFolder.has(row.file.toLowerCase())) {
      rowsWithoutBodies.push(row.file);
      continue;
    }
    if (row.skip !== undefined) skipped.push({ file: row.file, reason: row.skip });
    else packageable.push(row);
  }
  return {
    bodiesWithoutRows: folderGlbs.filter((f) => !inSheet.has(f.toLowerCase())),
    rowsWithoutBodies,
    packageable,
    skipped,
  };
}

export interface FillInputs {
  /** sha256 of the body's bytes, hex — the seed for every deterministic fill. */
  readonly sha256: string;
  /** The live roster (prod's), for the same-name id; null = unreachable, so a name has no slot to keep. */
  readonly roster: Roster | null;
  /** Names already claimed — the sheet's other rows AND the roster's — so a filled name never collides. */
  readonly takenNames: Iterable<string>;
  /** Ids the sheet's OTHER rows claim (as filled so far): two names that slugify alike must not share a folder (Codex P1, #162 round three). */
  readonly takenIds: Iterable<string>;
  readonly pool?: readonly PoolFamily[];
}

export type FilledField = 'name' | 'id' | 'selectionClip';

export interface FilledRow {
  readonly row: RosterSheetRow;
  /** Which columns this fill wrote — the write-back says them. */
  readonly filled: readonly FilledField[];
  /** The roster row the id came from, when it did. */
  readonly replaces: { readonly id: string; readonly name: string } | null;
  /** Why a fill could not complete — the row is a REFUSAL, not a package. */
  readonly refusals: readonly string[];
  /** Said, never refused: a picker family World has not seen yet (a chip of one, or a typo). */
  readonly warnings: readonly string[];
}

/**
 * Resolve a row's name, id and selection clip, writing what was derived.
 * Pure; the caller hashes the body and fetches the roster. A row already
 * complete passes through untouched (`filled` empty) — the sheet is the
 * source of truth once written.
 */
export function fillRow(sheet: RosterSheet, row: RosterSheetRow, inputs: FillInputs): FilledRow {
  const filled: FilledField[] = [];
  const refusals: string[] = [];
  let next: RosterSheetRow = { ...row };
  // 1. The name: the row's, else the pool's by hash — never a filename.
  if (next.name === undefined) {
    try {
      next = { ...next, name: pickName(inputs.sha256, inputs.takenNames, inputs.pool ?? NAMES_POOL).name };
      filled.push('name');
    } catch (err) {
      refusals.push(err instanceof Error ? err.message : String(err));
    }
  }
  // 2. The id: the row's, else the roster's same-name row (the slot is kept), else the slug.
  let replaces: FilledRow['replaces'] = null;
  let replacedTags: readonly string[] | null = null;
  if (next.name !== undefined) {
    let match: ReturnType<typeof findRosterRow> = null;
    // A roster defect (two rows of one name) is a refusal AND leaves the id
    // UNSET: a slug written back here would be found BY ID on the next run,
    // before the name check, and the defect would be masked for good
    // (Codex P1, #162 round three).
    let lookupFailed = false;
    if (inputs.roster !== null) {
      try {
        match = findRosterRow(inputs.roster, { ...(next.id !== undefined ? { id: next.id } : {}), name: next.name });
      } catch (err) {
        lookupFailed = true;
        refusals.push(err instanceof Error ? err.message : String(err));
      }
    }
    if (match !== null) {
      replaces = { id: match.row.id, name: match.row.name };
      replacedTags = match.row.tags;
    }
    if (next.id === undefined && !lookupFailed) {
      try {
        const id = match !== null ? match.row.id : slugify(next.name);
        // Two names that slugify alike ("Joan of Arc", "Joan-of-Arc") would
        // share `<id>/` and the second would overwrite the first (Codex P1,
        // #162 round three): a generated id another row holds is refused,
        // the id left unset, the row's author asked to name one.
        const taken = new Set(inputs.takenIds);
        if (taken.has(id)) {
          refusals.push(`id "${id}" is already another row's — "${next.name}" slugifies onto it; give one of the two rows its own id`);
        } else {
          next = { ...next, id };
          filled.push('id');
        }
      } catch (err) {
        refusals.push(err instanceof PackageRefusal ? err.reasons.join('; ') : err instanceof Error ? err.message : String(err));
      }
    }
  }
  // 3. The selection clip: the row's, else the sheet's pool by hash; no pool = the row must say.
  if (next.selectionClip === undefined) {
    if (sheet.selectionPool.length === 0) {
      refusals.push(`"${row.file}" names no selectionClip and the sheet's selectionPool is empty — name one or fill the pool`);
    } else {
      const idx = Number.parseInt(inputs.sha256.slice(8, 16), 16) % sheet.selectionPool.length;
      next = { ...next, selectionClip: sheet.selectionPool[idx]! };
      filled.push('selectionClip');
    }
  }
  // The compose's own resolver, on the tags choicesForRow will hand it: a carried odd value
  // (family:sky-pirates on the replaced row) is warned HERE too, so the batch report says it (Codex P2, #231).
  const picker = resolvePickerFacets(
    { tags: [...new Set([...sheet.defaults.tags, ...(next.tags ?? [])])], ...(next.pickerFacets !== undefined ? { pickerFacets: next.pickerFacets } : {}) },
    replacedTags,
  );
  const warnings = [...picker.warnings];
  return { row: next, filled, replaces, refusals, warnings };
}

/** A sheet with one row replaced by its filled form (by file, case-insensitively). */
export function withFilledRow(sheet: RosterSheet, filled: RosterSheetRow): RosterSheet {
  const key = filled.file.toLowerCase();
  const i = sheet.rows.findIndex((r) => r.file.toLowerCase() === key);
  if (i < 0) throw new Error(`"${filled.file}" has no row in the sheet to fill`);
  const rows = sheet.rows.slice();
  rows[i] = filled;
  return { ...sheet, rows };
}

/**
 * The Document's package choices for a FILLED row: the sheet's defaults
 * under the row's columns, the height declared either way, no takes yet
 * (the batch adds the headshot). Refuses a row the fill left incomplete.
 */
export function choicesForRow(sheet: RosterSheet, row: RosterSheetRow): PackageChoices {
  const reasons: string[] = [];
  if (row.name === undefined) reasons.push(`"${row.file}" has no name`);
  if (row.id === undefined) reasons.push(`"${row.file}" has no id`);
  if (row.selectionClip === undefined) reasons.push(`"${row.file}" has no selectionClip`);
  if (row.skip !== undefined) reasons.push(`"${row.file}" is skipped: ${row.skip}`);
  if (reasons.length > 0) throw new PackageRefusal(reasons);
  const roles = row.roles ?? sheet.defaults.roles;
  const npc = row.npc ?? (roles.includes('npc') ? sheet.defaults.npc : undefined);
  if (roles.includes('npc') && npc === undefined) throw new PackageRefusal([`"${row.file}" has the npc role and no archetype — the row or the sheet's defaults must name one`]);
  // A row's npc block under roles (its own or the defaults') without npc is a
  // contradiction the parse cannot see when the roles come from the defaults.
  if (row.npc !== undefined && !roles.includes('npc')) throw new PackageRefusal([`"${row.file}" carries an npc block but its roles (${roles.join(' + ')}) do not include npc`]);
  const tags = [...new Set([...sheet.defaults.tags, ...(row.tags ?? [])])];
  return {
    id: row.id as string,
    name: row.name as string,
    artist: row.artist ?? sheet.defaults.artist,
    ...(tags.length > 0 ? { tags } : {}),
    ...(row.pickerFacets !== undefined ? { pickerFacets: row.pickerFacets } : {}),
    roles,
    facets: row.facets ?? sheet.defaults.facets,
    heightMeters: row.heightMeters !== undefined ? { value: row.heightMeters, provenance: 'declared' } : { value: sheet.defaults.heightMeters, provenance: 'default' },
    selectionClip: row.selectionClip as string,
    pins: [],
    ...(npc !== undefined && roles.includes('npc') ? { npc } : {}),
    heroTakes: [],
  };
}
